import { Component, computed, effect, inject, input } from '@angular/core';
import { CdkMenuItem } from '@angular/cdk/menu';
import { Router } from '@angular/router';
import {
  ButtonDirective,
  ConfirmationDialogComponent,
  CuiDropdownComponent,
  CuiDropdownTriggerDirective,
  CuiDialogService,
} from '@innersource/common-ui-components';
import { ToolJobService } from '../../../services/api-services/tools/tool-job.service';
import { ToolchainJobService } from '../../../services/api-services/toolchains/toolchain-job.service';
import {
  getJobRunPresentation,
  getJobRunStatusPresentation,
  isFinishedJobStatus,
} from '../../../utils/job-run-presentation';
import { getDisplayName, Tool, ToolJob } from '../../../utils/models/tool.models';
import { Toolchain, ToolchainJob } from '../../../utils/models/toolchain.models';

export type JobPageHeaderState = {
  isTool: boolean;
  jobId: string | null;
  resource: Tool | Toolchain | undefined;
  run: ToolJob | ToolchainJob | undefined;
  loading: boolean;
  failed: boolean;
  waitingForInput: boolean;
  starting: boolean;
  reload: () => void;
};

const JOB_REFRESH_INTERVAL_MS = 2000;

@Component({
  selector: 'app-job-page-header',
  templateUrl: './job-page-header.component.html',
  styleUrls: ['./job-page-header.component.scss'],
  imports: [ButtonDirective, CdkMenuItem, CuiDropdownComponent, CuiDropdownTriggerDirective],
})
export class JobPageHeaderComponent {
  /** Inputs. */
  readonly state = input.required<JobPageHeaderState>();

  /** Services. */
  private readonly router = inject(Router);
  private readonly dialog = inject(CuiDialogService);
  private readonly toolJobService = inject(ToolJobService);
  private readonly toolchainJobService = inject(ToolchainJobService);

  /** Returns the current run's display presentation. */
  readonly presentation = computed(() => {
    const { run, resource } = this.state();
    const resourceTitle = resource ? getDisplayName(resource) : undefined;
    return run
      ? getJobRunPresentation(run, resourceTitle)
      : { title: '', ...getJobRunStatusPresentation('Pending') };
  });

  /** Returns the job page title. */
  readonly title = computed(() => {
    const { isTool, jobId, resource } = this.state();
    const resourceTitle = resource ? getDisplayName(resource) : undefined;
    const fallback = isTool ? 'Tool Run' : 'Toolchain Run';
    let title = jobId ? (this.presentation().title ?? resourceTitle) : resourceTitle;
    title ??= fallback;
    return jobId ? `${title} #${jobId}` : title;
  });

  /** Checks whether job actions are unavailable. */
  readonly actionsDisabled = computed(
    () => !this.numericJobId() || !isFinishedJobStatus(this.state().run?.status)
  );

  /** Returns a valid numeric job ID. */
  readonly numericJobId = computed(() => {
    const jobId = Number(this.state().jobId);
    return Number.isInteger(jobId) && jobId > 0 ? jobId : undefined;
  });

  /** Checks whether the job should be refreshed automatically. */
  readonly isAutoRefreshing = computed(
    () =>
      this.state().starting ||
      (!!this.numericJobId() &&
        !this.state().failed &&
        !this.state().waitingForInput &&
        !isFinishedJobStatus(this.state().run?.status))
  );

  /** Checks whether the activity spinner should be shown. */
  readonly showSpinner = computed(() => this.state().loading || this.isAutoRefreshing());

  /** Constructor. */
  constructor() {
    // Reload unfinished jobs while they are actively progressing.
    effect(onCleanup => {
      if (!this.isAutoRefreshing() || this.state().loading || this.state().starting) {
        return;
      }

      const timeoutId = setTimeout(() => this.state().reload(), JOB_REFRESH_INTERVAL_MS);
      onCleanup(() => clearTimeout(timeoutId));
    });
  }

  /** Opens confirmation for deleting the current job. */
  requestDelete(): void {
    const { isTool, jobId, run } = this.state();
    const numericJobId = Number(jobId);
    if (!numericJobId || !run) return;

    const resourceId = 'toolJobId' in run ? run.tool.toolId : run.toolchain.toolchainId;
    this.dialog.open(ConfirmationDialogComponent, {
      size: 'small',
      ariaLabel: 'Delete Job',
      data: {
        confirmTitle: 'Delete Job',
        confirmMessage: 'Are you sure you want to delete this job? This cannot be undone.',
        onConfirm: async () => {
          if (isTool) await this.toolJobService.deleteJobById(resourceId, numericJobId);
          else await this.toolchainJobService.deleteToolchainJob(resourceId, numericJobId);

          void this.router.navigate(isTool ? ['/tools', resourceId] : ['/toolchains', resourceId]);
        },
      },
    });
  }
}
